import { z } from 'zod';
import { Role } from '../enums.js';
import {
  Email,
  IsoDate,
  IsoDateTime,
  NonEmptyName,
  Pence,
  Phone,
  Slug,
  Uuid,
} from '../primitives.js';
import { TimeOffReason } from './scheduling.js';

/**
 * WS11 — Independent stylists & multi-salon chair renters (brief 11; ADR-0012 client-book portability,
 * ADR-0013 practitioner billing matrix). THE SHARED CONTRACT for the practitioner layer: residencies
 * (days↔salons), a salon-less private practice, the unified "My week" diary, and the portable sealed
 * book. Feature agents IMPORT these — they do NOT define their own.
 *
 * Architecture invariants these types encode (all from the ACCEPTED ADRs — do not weaken):
 *   • No second tenancy model. A residency is an existing salon membership (a `staff` row, role
 *     'chair_renter') + a schedule (working_hours). Private practice is the practitioner's OWN one-chair
 *     `salon` tenant (role 'owner'). Everything reuses the existing salons/staff/clients/appointments.
 *   • RLS is the ONLY isolation boundary. Every aggregate ("My week", portable book) is a READ-SIDE
 *     UNION: membershipsFor(userId) resolves identity (system pool), then each leg is a NORMAL
 *     withTenant() read under THAT membership's ctx, unioned in app memory and TAGGED by salon for the
 *     location label. The `salonId`/`salonName` on these items is that per-leg tag, never a cross-tenant
 *     join (ADR-0012 §2).
 *   • Sealed books are practitioner-owned but NEVER cross-tenant. The portable book includes ONLY
 *     memberships where the caller is a renter (renterId set); shared books (renter_id IS NULL) can
 *     never match by construction (ADR-0012 §3). Writes always target exactly ONE tenant.
 *   • "Sick is sick everywhere": practitioner-level time off is GLOBAL (keyed on the human/users.id),
 *     read by the cross-tenant double-booking guard, NOT a tenant-RLS row (ADR-0012/ADR-0013; brief).
 */

// ── Practitioner-level (cross-residency) time off ──────────────────────────────
// GLOBAL to the human — spans every residency + private practice at once. Persisted in the global
// `practitioner_time_off` table (system pool, REVOKED from the app pool), guarded at the API surface by
// the SESSION's user_id (never a request param for userId). Reuses the scheduling TimeOffReason enum so
// the practitioner surface and the per-salon staff_time_off surface read the same vocabulary.

export const PractitionerTimeOff = z
  .object({
    id: Uuid,
    /** The human this applies to (users.id). Resolved from the session — never accepted from the body. */
    userId: Uuid,
    startDate: IsoDate,
    endDate: IsoDate.describe('inclusive — the practitioner is off through this date, EVERYWHERE'),
    reason: TimeOffReason,
    note: z.string().max(500).nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PractitionerTimeOff' });
export type PractitionerTimeOff = z.infer<typeof PractitionerTimeOff>;

/**
 * Create practitioner-level time off. The owning user is ALWAYS the authenticated session — there is no
 * userId field here, by design (it would be an authorization hole; ADR-0012/0013). The double-booking
 * guard reads this across all the caller's tenants — "sick is sick everywhere".
 */
export const CreatePractitionerTimeOff = z
  .object({
    startDate: IsoDate,
    endDate: IsoDate,
    reason: TimeOffReason.default('holiday'),
    note: z.string().max(500).nullish(),
  })
  .meta({ id: 'CreatePractitionerTimeOff' });
export type CreatePractitionerTimeOff = z.infer<typeof CreatePractitionerTimeOff>;

/**
 * Optional date-range filter for listing the caller's own practitioner-level time off.
 * NOTE: querystring schemas are NOT given a `.meta({id})` — the codebase idiom (every other *Query
 * entity) keeps them anonymous so @fastify/swagger inlines the params; a registered component here makes
 * the parameters a $ref that resolveCommonParams cannot expand, breaking the OpenAPI emit.
 */
export const PractitionerTimeOffQuery = z.object({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export type PractitionerTimeOffQuery = z.infer<typeof PractitionerTimeOffQuery>;

// ── Residency summary ──────────────────────────────────────────────────────────
// A residency is NOT a new entity — it is a VIEW of one of the caller's memberships (a `staff` row in a
// salon tenant) enriched with its working schedule + a location label. Resolved from membershipsFor()
// (identity) + a per-leg withTenant() read of that membership's working_hours. `active`/`endedAt` reflect
// the staff row's lifecycle (ending a residency = active→false + residency_ended_at stamped; the salon's
// history is untouched — ADR-0012 §4).

/** A single weekly working block for a residency (which day/hours the practitioner works there). */
export const ResidencyDay = z
  .object({
    weekday: z.int().min(0).max(6).describe('0 = Sunday … 6 = Saturday'),
    startTime: z.string().describe('wall-clock "HH:MM" in the salon timezone'),
    endTime: z.string(),
  })
  .meta({ id: 'ResidencyDay' });
export type ResidencyDay = z.infer<typeof ResidencyDay>;

export const Residency = z
  .object({
    /** The salon tenant this residency is in (the per-leg tag; never a cross-tenant join). */
    salonId: Uuid,
    salonName: NonEmptyName.describe('for the location label, e.g. "Salon A, Marylebone"'),
    /** The caller's `staff.id` WITHIN this salon (their membership row). */
    staffId: Uuid,
    /** Non-null when the caller holds a sealed book here (drives portable-book inclusion). */
    renterId: Uuid.nullable(),
    role: Role,
    /**
     * Distinguishes a salon-less PRIVATE PRACTICE leg (the caller's own one-chair tenant, role 'owner')
     * from a chair-rental RESIDENCY in someone else's salon. Both are memberships; this is a UI/label hint.
     */
    isPrivatePractice: z.boolean(),
    active: z.boolean().describe('false once the residency has been ended (staff.active)'),
    endedAt: IsoDateTime.nullable().describe('staff.residency_ended_at — advisory; null = active'),
    /** Which days/hours the practitioner works here (their working_hours in this tenant). */
    schedule: z.array(ResidencyDay).describe("this membership's weekly working blocks"),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Residency' });
export type Residency = z.infer<typeof Residency>;

// ── Unified diary — "My week" ──────────────────────────────────────────────────
// One appointment from the read-side union across all the caller's residencies + private practice,
// TAGGED with the place it belongs to so the diary can colour-code + label by location (ADR-0012 §2;
// brief 11 §3). Each item came from a NORMAL withTenant() read of one tenant — the salonId/salonName are
// that leg's tag. Creating/editing routes back to THAT tenant's own context (a single-tenant write).

export const MyWeekItem = z
  .object({
    appointmentId: Uuid,
    /** The tenant this appointment belongs to (the per-leg tag — write back here, never elsewhere). */
    salonId: Uuid,
    salonName: NonEmptyName.describe('location label for the diary, e.g. "Salon A, Marylebone"'),
    isPrivatePractice: z.boolean().describe("true for the practitioner's own studio leg"),
    staffId: Uuid.describe("the caller's staff.id in THIS tenant"),
    serviceId: Uuid,
    clientId: Uuid.nullable(),
    clientDisplayName: z.string().max(160),
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    status: z.string().describe('AppointmentStatus of the underlying booking'),
    priceQuoted: z.int().nonnegative(),
  })
  .meta({ id: 'MyWeekItem' });
export type MyWeekItem = z.infer<typeof MyWeekItem>;

/**
 * Window for the "My week" aggregation. The union fans out over the caller's own memberships only.
 * Anonymous (no `.meta({id})`) per the querystring idiom — see PractitionerTimeOffQuery's note.
 */
export const MyWeekQuery = z.object({
  from: IsoDateTime.describe('inclusive window start'),
  to: IsoDateTime.describe('exclusive window end'),
});
export type MyWeekQuery = z.infer<typeof MyWeekQuery>;

// ── Portable sealed book ────────────────────────────────────────────────────────
// One sealed client from the read-side union over ONLY the memberships where the caller is a renter
// (renterId set). For each such leg the orchestrator opens withTenant({ salonId, renterId: staffId })
// and reads the renter's OWN clients; shared clients (renter_id IS NULL) can never match because no NULL
// renter predicate is ever expressed (ADR-0012 §3). Each entry is tagged with the salon it physically
// lives in — the row never moves; the person's access spans tenants via the union.

export const PortableBookEntry = z
  .object({
    clientId: Uuid,
    /** The salon tenant this sealed client physically lives in (rows never travel; ADR-0012 §4). */
    salonId: Uuid,
    salonName: NonEmptyName.describe('where this client was created / is held'),
    isPrivatePractice: z.boolean(),
    /** The renter (caller's staff.id in that tenant) who owns this sealed row. */
    renterId: Uuid,
    firstName: NonEmptyName,
    lastName: z.string().max(120).nullable(),
    email: Email.nullable(),
    phone: z.string().max(20).nullable(),
    marketingConsent: z
      .boolean()
      .describe('travels with the practitioner-as-controller for sealed rows (ADR-0012 §GDPR)'),
    lifetimeValue: z.int().nonnegative(),
    visitCount: z.int().nonnegative(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PortableBookEntry' });
export type PortableBookEntry = z.infer<typeof PortableBookEntry>;

/**
 * Optional filter for the portable-book aggregation (text search over the caller's own sealed rows).
 * Anonymous (no `.meta({id})`) per the querystring idiom — see PractitionerTimeOffQuery's note.
 */
export const PortableBookQuery = z.object({
  search: z.string().trim().max(120).optional(),
});
export type PortableBookQuery = z.infer<typeof PortableBookQuery>;

// ── Private-practice signup ──────────────────────────────────────────────────────
// Creating a salon-less practitioner's OWN one-chair tenant (brief 11 §1; ADR-0013 §4 — a salon whose
// owner is its only chair, NO special SKU). Two shapes: a brand-new account (no session yet — like
// /register), and an EXISTING logged-in practitioner adding a studio to the account they already use for
// residencies (their second-or-later membership). Solo is a one-chair tenant via the Math.max(1,…)
// floor; the practitioner becomes the role='owner' staff row of the new salon.

/** Brand-new practitioner: create the account AND their private-practice tenant in one step. */
export const PrivatePracticeSignup = z
  .object({
    email: Email,
    password: z.string().min(10).max(200),
    displayName: NonEmptyName,
    /** The studio/practice name (their own one-chair salon tenant). */
    practiceName: NonEmptyName,
    /** Public slug for book.salonomy.com/<slug> — their personal booking presence (WS8). */
    slug: Slug,
  })
  .meta({ id: 'PrivatePracticeSignup' });
export type PrivatePracticeSignup = z.infer<typeof PrivatePracticeSignup>;

/**
 * Existing practitioner (already has an account via a residency) opening their OWN studio — adds a new
 * salon tenant to the SAME login. No new credentials; the new salon is owned by the authenticated user.
 * One login across their whole working life (brief 11 non-negotiable #7).
 */
export const AddPrivatePractice = z
  .object({
    practiceName: NonEmptyName,
    slug: Slug,
  })
  .meta({ id: 'AddPrivatePractice' });
export type AddPrivatePractice = z.infer<typeof AddPrivatePractice>;

// ── Personal booking handle (the practitioner's global "book me, wherever I work" identity) ──────
// A single, cross-residency public booking handle keyed on the HUMAN (users.id). Resolved BEFORE any
// tenant context (handle → user → memberships), it is an IDENTITY-PLANE fact like the portable book —
// NOT a tenant-RLS row. The owning user is ALWAYS the authenticated session for writes (never a body
// param); the public read is by handle (a deliberately public key). Persisted in the global
// `practitioner_booking_handles` table (migration 0025; system pool, REVOKED from the app pool).

export const PractitionerBookingHandle = z
  .object({
    /** The public slug a client books by — book.salonomy.com/p/<handle>. Globally unique (case-insensitive). */
    handle: Slug,
    /** What the client sees on the personal booking page (the practitioner's presented name). */
    displayName: NonEmptyName,
    /** Opt-in switch: a handle can be reserved but temporarily hidden from public resolution. */
    enabled: z.boolean(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PractitionerBookingHandle' });
export type PractitionerBookingHandle = z.infer<typeof PractitionerBookingHandle>;

/**
 * Claim / update the caller's personal booking handle. The owning user is ALWAYS the authenticated
 * session — there is NO userId field here, by design (it would be an authorization hole). A
 * case-insensitive duplicate of someone else's handle is a 409.
 */
export const UpsertBookingHandle = z
  .object({
    handle: Slug,
    displayName: NonEmptyName,
    /** Defaults to enabled on claim; can be flipped to hide the page without giving up the handle. */
    enabled: z.boolean().default(true),
  })
  .meta({ id: 'UpsertBookingHandle' });
export type UpsertBookingHandle = z.infer<typeof UpsertBookingHandle>;

// ── Personal booking page — book.salonomy.com/p/<handle> ─────────────────────────────────────────
// WS8 stitched at the IDENTITY layer. A handle resolves to a HUMAN, and the page offers that human's
// online-bookable services + slots across EVERY residency + private practice, each TAGGED by location.
// Each leg is a NORMAL single-tenant withTenant() read of the UNCHANGED WS8 engine, FILTERED to the
// practitioner's OWN chair in that tenant and gated by the per-staff online_bookable + the salon
// online_booking_enabled flags. The union is in app memory; no statement spans two salon_ids. Deposits
// /policies follow the SLOT's tenant — /start + /confirm delegate to that tenant's existing WS8 seam.

/** One online-bookable service offered through a personal booking page, tagged by the salon it lives in. */
export const PersonalBookableService = z
  .object({
    /** The service id WITHIN its salon (book the slot's service in the slot's salon). */
    serviceId: Uuid,
    name: z.string(),
    category: z.string().nullable(),
    durationMin: z.int(),
    priceFrom: Pence,
    requiresPatchTest: z.boolean(),
    policyText: z.string(),
    depositText: z.string(),
    cancellationText: z.string(),
    noShowText: z.string(),
    // ── Location tag (the per-leg salon; never a cross-tenant join). ──
    salonSlug: Slug,
    salonName: z.string(),
    /** A human-readable location label for the page (the salon's name; the studio for private practice). */
    location: z.string(),
    /** The practitioner's OWN staff.id in THIS salon (the only chair this page books). */
    staffId: Uuid,
    isPrivatePractice: z.boolean(),
  })
  .meta({ id: 'PersonalBookableService' });
export type PersonalBookableService = z.infer<typeof PersonalBookableService>;

/** The personal booking page's services payload: the practitioner's identity + the unioned services. */
export const PersonalBookableServices = z
  .object({
    handle: Slug,
    displayName: z.string(),
    services: z.array(PersonalBookableService),
  })
  .meta({ id: 'PersonalBookableServices' });
export type PersonalBookableServices = z.infer<typeof PersonalBookableServices>;

/**
 * Personal availability query: a service + a date. The service id is scoped to its OWN salon, so the
 * query also carries the salonSlug the service belongs to (a handle's services span tenants). Anonymous
 * (no `.meta({id})`) per the querystring idiom — see PractitionerTimeOffQuery's note.
 */
export const PersonalAvailabilityQuery = z.object({
  salonSlug: Slug,
  serviceId: Uuid,
  date: IsoDate,
  granularityMin: z.coerce.number().int().min(5).max(120).default(15),
});
export type PersonalAvailabilityQuery = z.infer<typeof PersonalAvailabilityQuery>;

/** A single personal-booking slot — the practitioner's own chair in ONE tenant, tagged by location. */
export const PersonalSlot = z
  .object({
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    staffId: Uuid.describe("the practitioner's staff.id in the slot's salon"),
    staffName: z.string(),
    salonSlug: Slug,
    salonName: z.string(),
    location: z.string(),
    isPrivatePractice: z.boolean(),
  })
  .meta({ id: 'PersonalSlot' });
export type PersonalSlot = z.infer<typeof PersonalSlot>;

export const PersonalAvailabilityResult = z
  .object({
    serviceId: Uuid,
    salonSlug: Slug,
    date: IsoDate,
    slots: z.array(PersonalSlot),
  })
  .meta({ id: 'PersonalAvailabilityResult' });
export type PersonalAvailabilityResult = z.infer<typeof PersonalAvailabilityResult>;

/**
 * Start a personal booking: the chosen slot carries the salonSlug + staffId that identify WHICH of the
 * practitioner's chairs (and therefore WHICH tenant's deposit/policy) the booking lands in. The server
 * re-resolves the handle → user and ASSERTS {salonSlug, staffId} is a genuine active online-bookable leg
 * of THIS human before delegating to that tenant's WS8 /start seam — a forged salonSlug is a 400.
 */
export const StartPersonalBooking = z
  .object({
    name: NonEmptyName,
    email: Email.optional(),
    phone: Phone.optional(),
    /** The salon the chosen slot belongs to (must be a genuine active leg of this handle's human). */
    salonSlug: Slug,
    serviceId: Uuid,
    variantId: Uuid.optional(),
    /** The practitioner's chair in that salon (must be the handle's own staff row there). */
    staffId: Uuid,
    startAt: IsoDateTime,
    notes: z.string().max(2000).optional(),
  })
  .refine((b) => Boolean(b.email || b.phone), {
    message: 'Provide an email or a phone number so we can confirm your booking',
    path: ['email'],
  })
  .meta({ id: 'StartPersonalBooking' });
export type StartPersonalBooking = z.infer<typeof StartPersonalBooking>;

/**
 * Confirm a personal booking with the verification code. Carries the salonSlug + staffId again so the
 * confirm re-asserts the leg (forged salonSlug → 400) and routes to the SAME tenant's WS8 /confirm seam,
 * inheriting policy-freeze, deposit-on-that-tenant's-account, advisory-locked create and variant freeze
 * UNCHANGED — plus the cross-tenant double-book guard keyed on the resolved human.
 */
export const ConfirmPersonalBooking = z
  .object({
    holdId: Uuid,
    code: z.string().min(4).max(12),
    salonSlug: Slug,
    staffId: Uuid,
  })
  .meta({ id: 'ConfirmPersonalBooking' });
export type ConfirmPersonalBooking = z.infer<typeof ConfirmPersonalBooking>;
